import { useEffect, useState } from "react";
import { referenceClassifier } from '../utils/referenceNodes';

/**
 * Custom hook to produce graph data from an array map.
 * @param {*} arrayMap 
 * @returns {Object} - The graph data (nodes and links) and details (all other data).
 */

function useGraphData(arrayMap){

  const [graphData, setGraphData] = useState({ nodes: [], links: [] });
  const [details, setDetails] = useState({documents: [], text_units: [], communities: [], community_reports: [], covariates: []});

  useEffect(() => {

    const {
      entities = [],
      relationships = [],
      documents = [],
      text_units = [],
      communities = [],
      community_reports = [],
      covariates = [],
    } = arrayMap || {};

    const isReferenceOnly = referenceClassifier(documents, text_units);
    const nodes = entities.map((entity) => ({
      uuid: entity.id,
      id: entity.title,
      name: entity.display_name || entity.title,
      title: entity.display_name || entity.title,
      type: entity.type,
      category_tags: entity.category_tags ? String(entity.category_tags).split(';') : [entity.type],
      context_subtype: entity.context_subtype,
      concept_type: entity.concept_type,
      study_name: entity.study_name,
      event_time: entity.event_time ? JSON.parse(entity.event_time) : null,
      aliases: entity.aliases ? JSON.parse(entity.aliases) : [],
      sources: entity.sources ? String(entity.sources).split(';') : [],
      source_count: entity.source_count,
      source_roles: entity.source_roles,
      reference_only: entity.schema_version && typeof entity.reference_only === 'boolean' ? entity.reference_only : isReferenceOnly(entity),
      needs_review: entity.needs_review,
      description: entity.description,
      human_readable_id: entity.human_readable_id,
      text_unit_ids: entity.text_unit_ids,
      neighbors: [],
      links: [],
    }));

    const nodesMap = {};
    nodes.forEach(node => nodesMap[node.id] = node);

    const links = relationships
      .map((relationship) => ({
        source: relationship.source,
        target: relationship.target,
        type: relationship.type,
        weight: relationship.weight,
        description: relationship.description,
        text_unit_ids: relationship.text_unit_ids,
        id: relationship.id,
        human_readable_id: relationship.human_readable_id,
        combined_degree: relationship.combined_degree,
      }))
      .filter((link) => nodesMap[link.source] && nodesMap[link.target]);

    //Make sure that each link has a corresponding source and target node
    links.forEach(link => {
      const sourceNode = nodesMap[link.source];
      const targetNode = nodesMap[link.target];
      if (sourceNode && targetNode) {
        if (!sourceNode.neighbors.includes(targetNode))
          sourceNode.neighbors.push(targetNode);
        if (!targetNode.neighbors.includes(sourceNode))
          targetNode.neighbors.push(sourceNode);
        if (!sourceNode.links.includes(link))
          sourceNode.links.push(link);
        if (!targetNode.links.includes(link))
          targetNode.links.push(link);
      }
    });

    // Set nodes and links as Graph Data as only these two are needed for graph
    if (nodes.length > 0) {
      setGraphData({ nodes, links });
    }

    // Keep all other data in details for other purposes
    setDetails({
      documents,
      text_units,
      communities,
      community_reports,
      covariates,
    });
  }, [
    arrayMap
  ]);

  return {graphData, details};
};

export default useGraphData;
